import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Box, Chip, MenuItem, Stack, TextField } from '@mui/material';
import { listAssignmentHistory } from '../../api/leads';
import { AssignmentHistoryEntry } from '../../types/lead';
import { TableToolbar } from '../../components/table/TableToolbar';
import { useCounselorOptions } from '../../hooks/useCounselorOptions';

const TYPE_LABELS: Record<string, string> = {
  UNASSIGNED: 'Unassigned',
  COUNSELOR_CREATED: 'Counselor Created',
  CENTRAL_ASSIGNED: 'Central Assigned',
  TRANSFERRED: 'Transferred',
};

interface AssignmentHistoryTableProps {
  /** Scope the table to a single lead's history when embedded elsewhere;
   * omitted for the pool-wide view. */
  leadId?: number;
}

/** Pool-wide (or single-lead, via `leadId`) assignment/reassignment
 * history — lead, previous counselor, new counselor, changed by, date/
 * time, assignment type, reason. Shared by the Central Lead Distribution
 * page's "Assignment History" section and any other admin view that
 * needs this same data (avoids building the aggregate history twice). */
export const AssignmentHistoryTable = ({ leadId }: AssignmentHistoryTableProps) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [counselorId, setCounselorId] = useState<number | ''>('');

  const { counselors } = useCounselorOptions(!leadId);

  const historyQuery = useQuery({
    queryKey: ['lead-assignment-history', leadId, counselorId, paginationModel.page, paginationModel.pageSize],
    queryFn: () =>
      listAssignmentHistory({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        leadId,
        counselorId: counselorId || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const columns: GridColDef<AssignmentHistoryEntry>[] = [
    {
      field: 'lead',
      headerName: 'Lead',
      flex: 1,
      valueGetter: (_v, row) => `${row.lead.leadNumber} — ${row.lead.firstName} ${row.lead.lastName}`,
    },
    {
      field: 'fromUser',
      headerName: 'Previous counselor',
      width: 170,
      valueGetter: (_v, row) => (row.fromUser ? `${row.fromUser.firstName} ${row.fromUser.lastName}` : '— Unassigned'),
    },
    {
      field: 'toUser',
      headerName: 'New counselor',
      width: 170,
      valueGetter: (_v, row) => (row.toUser ? `${row.toUser.firstName} ${row.toUser.lastName}` : '—'),
    },
    {
      field: 'assignmentType',
      headerName: 'Type',
      width: 160,
      renderCell: (params) =>
        params.value ? <Chip size="small" label={TYPE_LABELS[params.value] ?? params.value} /> : '—',
    },
    {
      field: 'changedBy',
      headerName: 'Changed by',
      width: 160,
      valueGetter: (_v, row) => `${row.changedBy.firstName} ${row.changedBy.lastName}`,
    },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 1,
      valueGetter: (_v, row) => row.reason ?? '—',
    },
    {
      field: 'changedAt',
      headerName: 'Date / time',
      width: 170,
      valueGetter: (_v, row) => new Date(row.changedAt).toLocaleString(),
    },
  ];

  return (
    <Box>
      {!leadId && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Counselor"
            sx={{ width: 200 }}
            value={counselorId}
            onChange={(e) => {
              setCounselorId(e.target.value ? Number(e.target.value) : '');
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {counselors.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.firstName} {c.lastName}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      )}
      <Box sx={{ height: 480, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={historyQuery.data?.data ?? []}
          columns={columns}
          rowCount={historyQuery.data?.meta.total ?? 0}
          loading={historyQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          slots={{ toolbar: TableToolbar }}
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
};
